import { type TFunction, t as translate } from 'i18next';
import * as yup from 'yup';

import {
  type AggregationFn,
  type FilterType,
  findCatalogMetricFromQuery,
  getAggregationOptionsForMetric,
  getCategories,
  getDefaultAggregationForMetric,
  getFilterOptionsForMetric,
  getMetricById,
} from '@/modules/analytics/constants/metricsCatalog';
import {
  type ReportFilters,
  parseFilters,
  serializeFilters,
} from '@/modules/analytics/utils/reportFilters';
import type { MetricsQueryRequest } from '@/shared/types/metricsQuery';
import type { MetricRange, ReportMetric } from '@/shared/types/reports';

export type MetricDraft = {
  metricId: string;
  aggregationFn: AggregationFn | '';
  groupBy: string[];
  filters: ReportFilters;
  range: MetricRange | null;
};

export const EMPTY_METRIC_DRAFT: MetricDraft = {
  metricId: '',
  aggregationFn: '',
  groupBy: [],
  filters: {},
  range: null,
};

export const metricDraftSchema = yup.object({
  metricId: yup.string().required(() => translate('validation.required')),
  aggregationFn: yup.string().required(() => translate('validation.required')),
  groupBy: yup.array(yup.string().required()).required(),
  filters: yup.mixed<ReportFilters>().required(),
  range: yup
    .mixed<MetricRange>()
    .nullable()
    .required(() => translate('validation.required')),
});

export const getDraftForMetric = (metricId: string): Omit<MetricDraft, 'range'> => {
  const metric = getMetricById(metricId);
  return {
    metricId,
    aggregationFn: getDefaultAggregationForMetric(metricId) ?? '',
    groupBy: metric?.primaryDimension ? [metric.primaryDimension] : [],
    filters: {},
  };
};

export const toMetricDraft = (metric: ReportMetric): MetricDraft => {
  const catalog = findCatalogMetricFromQuery(metric.query, metric.metricId);
  const columnTypes = getFilterOptionsForMetric(catalog?.metric.id).reduce<
    Record<string, FilterType>
  >((acc, opt) => {
    acc[opt.column] = opt.type;
    return acc;
  }, {});
  return {
    metricId: catalog?.metric.id ?? '',
    aggregationFn: catalog?.aggregationFn ?? '',
    groupBy: metric.query.groupBy ?? [],
    filters: parseFilters(metric.query.filter, columnTypes),
    range: metric.range,
  };
};

export const getMetricDisplayName = (metric: ReportMetric, index: number, t: TFunction): string =>
  metric.name?.trim() || t('analytics.reports_detail_metric_placeholder', { 0: index + 1 });

export const getMetricTitle = (metricId: string): string | undefined => {
  const metric = getMetricById(metricId);
  if (!metric) {
    return undefined;
  }
  const category = getCategories().find((c) => c.id === metric.category);
  return category ? `${metric.label} - ${category.label}` : metric.label;
};

export const hasAggregationChoice = (metricId: string): boolean =>
  !getMetricById(metricId)?.derived && getAggregationOptionsForMetric(metricId).length > 1;

export const buildReportMetric = (draft: MetricDraft, name?: string): ReportMetric | null => {
  const catalogMetric = getMetricById(draft.metricId);
  if (!catalogMetric || !draft.range) {
    return null;
  }
  let aggregations: typeof catalogMetric.aggregations;
  if (catalogMetric.derived) {
    const required = new Set([catalogMetric.derived.numerator, catalogMetric.derived.denominator]);
    aggregations = catalogMetric.aggregations.filter((a) => required.has(a.alias));
  } else {
    const single = catalogMetric.aggregations.find((a) => a.function === draft.aggregationFn);
    if (!single) {
      return null;
    }
    aggregations = [single];
  }
  const filter = serializeFilters(draft.filters);
  const query: MetricsQueryRequest = {
    table: catalogMetric.table,
    aggregations: aggregations.map((a) => ({ ...a })),
    ...(draft.groupBy.length > 0 ? { groupBy: draft.groupBy } : {}),
    ...(filter ? { filter } : {}),
  };
  return {
    metricId: catalogMetric.id,
    name: name?.trim() || getMetricTitle(catalogMetric.id) || catalogMetric.label,
    query,
    range: draft.range,
  };
};
